import React, { useState } from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { shouldBreakPage } from '../pdfPageSpace';
import { RutaDto } from '../../../types';

interface RutasReportGeneratorProps {
  rutas: RutaDto[];
  fechaGeneracion: string;
  filtroFecha: string;
  usuarioSeleccionado?: number;
  usuariosDisponibles: Array<{ id: number; nombre: string }>;
}

const RutasReportGenerator: React.FC<RutasReportGeneratorProps> = ({
  rutas,
  fechaGeneracion,
  filtroFecha,
  usuarioSeleccionado,
  usuariosDisponibles
}) => {
  const [filtroUsuario, setFiltroUsuario] = useState<number | 'todos'>('todos');

  const generarReporteRutas = () => {
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageHeight = doc.internal.pageSize.height;
    const margin = 20;
    
    // Función auxiliar para verificar espacio y agregar página si es necesario
    const checkPageSpace = (requiredSpace: number, currentY: number) => {
      if (shouldBreakPage(currentY, requiredSpace, pageHeight, margin)) {
        doc.addPage();
        return margin;
      }
      return currentY;
    };
    
    // Configurar fuente
    doc.setFont('helvetica');
    
    // Título principal
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('Reporte de Rutas de Limpieza', 105, 20, { align: 'center' });
    
    // Información del reporte
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(`Período: ${filtroFecha}`, 20, 35);
    
    const usuarioTexto = filtroUsuario === 'todos' 
      ? 'Todos los usuarios' 
      : usuariosDisponibles.find(u => u.id === filtroUsuario)?.nombre || 'Usuario específico';
    doc.text(`Usuario: ${usuarioTexto}`, 20, 42);
    
    doc.text(`Fecha de generación: ${fechaGeneracion}`, 20, 49);
    doc.text(`Total de rutas: ${rutas.length}`, 20, 56);
    
    // Resumen ejecutivo
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Resumen Ejecutivo', 20, 75);
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    
    const totalAreas = rutas.reduce((sum, ruta) => sum + ruta.totalAreasLimpieza, 0);
    const totalTareasRealizadas = rutas.reduce((sum, ruta) => sum + ruta.totalTareasRealizadas, 0);
    const totalTareasPendientes = rutas.reduce((sum, ruta) => sum + ruta.totalTareasPendientes, 0);
    const totalRegistros = rutas.reduce((sum, ruta) => sum + ruta.registrosLimpieza.length, 0);
    
    doc.text(`• Total de áreas en limpieza: ${totalAreas}`, 25, 85);
    doc.text(`• Total de tareas realizadas: ${totalTareasRealizadas}`, 25, 92);
    doc.text(`• Total de tareas pendientes: ${totalTareasPendientes}`, 25, 99);
    doc.text(`• Total de registros de limpieza: ${totalRegistros}`, 25, 106);
    
    // Análisis por usuario
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Análisis por Usuario', 20, 125);
    
    const analisisPorUsuario = rutas.reduce((acc, ruta) => {
      const usuarioId = ruta.usuarioId;
      if (!acc[usuarioId]) {
        acc[usuarioId] = {
          nombre: ruta.usuarioNombre,
          totalRutas: 0,
          totalAreas: 0,
          totalTareasRealizadas: 0,
          totalTareasPendientes: 0,
          totalRegistros: 0
        };
      }
      
      acc[usuarioId].totalRutas++;
      acc[usuarioId].totalAreas += ruta.totalAreasLimpieza;
      acc[usuarioId].totalTareasRealizadas += ruta.totalTareasRealizadas;
      acc[usuarioId].totalTareasPendientes += ruta.totalTareasPendientes;
      acc[usuarioId].totalRegistros += ruta.registrosLimpieza.length;
      
      return acc;
    }, {} as Record<number, any>);
    
    const analisisArray = Object.values(analisisPorUsuario);
    
    // Tabla de análisis por usuario
    const tablaAnalisis = analisisArray.map(analisis => [
      analisis.nombre,
      analisis.totalRutas.toString(),
      analisis.totalAreas.toString(),
      analisis.totalTareasRealizadas.toString(),
      analisis.totalTareasPendientes.toString(),
      analisis.totalRegistros.toString()
    ]);
    
    autoTable(doc, {
      head: [['Usuario', 'Rutas', 'Áreas', 'Tareas Realizadas', 'Tareas Pendientes', 'Registros']],
      body: tablaAnalisis,
      startY: 135,
      headStyles: { fillColor: [59, 130, 246], textColor: 255, fontSize: 10 },
      bodyStyles: { fontSize: 9 },
      margin: { left: 20, right: 20 }
    });
    
    // Detalle de rutas
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    const finalY = (doc as any).lastAutoTable?.finalY || 150;
    let yPosition = checkPageSpace(25, finalY + 15);
    doc.text('Detalle de Rutas', 20, yPosition);
    yPosition += 10;
    
    rutas.forEach((ruta, index) => {
      // Verificar si necesitamos una nueva página para la ruta completa
      const rutaEstimatedHeight = 80; // Estimación del espacio necesario para una ruta
      yPosition = checkPageSpace(rutaEstimatedHeight, yPosition);
      
      // Encabezado de ruta
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(`Ruta ${index + 1}: ${ruta.usuarioNombre} - ${new Date(ruta.fecha).toLocaleDateString('es-ES')}`, 20, yPosition);
      
      yPosition += 8;
      
      // Detalles de la ruta
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`• Áreas en limpieza: ${ruta.totalAreasLimpieza}`, 25, yPosition);
      yPosition += 6;
      doc.text(`• Tareas realizadas: ${ruta.totalTareasRealizadas}`, 25, yPosition);
      yPosition += 6;
      doc.text(`• Tareas pendientes: ${ruta.totalTareasPendientes}`, 25, yPosition);
      yPosition += 6;
      doc.text(`• Registros de limpieza: ${ruta.registrosLimpieza.length}`, 25, yPosition);
      
      yPosition += 10;
      
      // Tabla de registros de la ruta
      if (ruta.registrosLimpieza.length > 0) {
        const tablaRegistros = ruta.registrosLimpieza.map(registro => [
          new Date(registro.fechaHoraInicio).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }),
          registro.areaNombre || 'N/A',
          registro.fechaHoraFin ? 'Completado' : 'Pendiente',
          registro.tiempoRealMinutos ? `${registro.tiempoRealMinutos}min` : 'N/A'
        ]);
        
        // Verificar espacio para la tabla
        const tablaEstimatedHeight = tablaRegistros.length * 10 + 30;
        yPosition = checkPageSpace(tablaEstimatedHeight, yPosition);
        
        autoTable(doc, {
          head: [['Hora', 'Área', 'Estado', 'Tiempo Real']],
          body: tablaRegistros,
          startY: yPosition,
          headStyles: { fillColor: [107, 114, 128], textColor: 255, fontSize: 9 },
          bodyStyles: { fontSize: 8 },
          margin: { left: 20, right: 20, top: 10, bottom: 10 }
        });
        
        yPosition = (doc as any).lastAutoTable?.finalY + 10 || yPosition + tablaEstimatedHeight;
      }
      
      yPosition += 5;
    });
    
    // Recomendaciones
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    // Importante: basar el inicio de recomendaciones en la posición actual,
    // no en la última autoTable (que podría ser la de "Análisis por Usuario")
    const finalYRecomendaciones = yPosition;
    let recomendacionesY = checkPageSpace(40, finalYRecomendaciones + 15);
    doc.text('Recomendaciones', 20, recomendacionesY);
    recomendacionesY += 10;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    
    const eficienciaPromedio = totalTareasRealizadas > 0 
      ? Math.round((totalTareasRealizadas / (totalTareasRealizadas + totalTareasPendientes)) * 100)
      : 0;
    
    recomendacionesY = checkPageSpace(15, recomendacionesY);
    if (eficienciaPromedio >= 90) {
      doc.text('• Excelente rendimiento general del equipo de limpieza', 25, recomendacionesY);
    } else if (eficienciaPromedio >= 75) {
      doc.text('• Buen rendimiento, considerar optimización de procesos', 25, recomendacionesY);
    } else {
      doc.text('• Oportunidad de mejora en la gestión de tareas pendientes', 25, recomendacionesY);
    }
    recomendacionesY += 15;
    
    if (totalTareasPendientes > totalTareasRealizadas * 0.2) {
      recomendacionesY = checkPageSpace(15, recomendacionesY);
      doc.text('• Revisar asignación de recursos para reducir tareas pendientes', 25, recomendacionesY);
      recomendacionesY += 15;
    }
    
    if (totalAreas > 0) {
      const areasPorUsuario = totalAreas / rutas.length;
      if (areasPorUsuario > 10) {
        recomendacionesY = checkPageSpace(15, recomendacionesY);
        doc.text('• Considerar redistribución de áreas para optimizar carga de trabajo', 25, recomendacionesY);
      }
    }
    
    // Guardar el PDF
    const nombreArchivo = `reporte-rutas-${filtroFecha.toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(nombreArchivo);
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6 mb-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Reporte de Rutas de Limpieza
          </h3>
          <p className="text-sm text-gray-600">
            Genera reportes detallados de las rutas de limpieza por usuario y período
          </p>
        </div>
        
        <div className="mt-4 lg:mt-0">
          <select
            value={filtroUsuario}
            onChange={(e) => setFiltroUsuario(e.target.value === 'todos' ? 'todos' : parseInt(e.target.value))}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm mr-3"
          >
            <option value="todos">Todos los usuarios</option>
            {usuariosDisponibles.map(usuario => (
              <option key={usuario.id} value={usuario.id}>
                {usuario.nombre}
              </option>
            ))}
          </select>
          
          <button
            onClick={generarReporteRutas}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
          >
            Generar Reporte de Rutas
          </button>
        </div>
      </div>
      
      {/* Vista previa de datos */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Vista Previa de Datos</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-gray-500">Total Rutas:</span>
            <span className="ml-2 font-medium">{rutas.length}</span>
          </div>
          <div>
            <span className="text-gray-500">Total Áreas:</span>
            <span className="ml-2 font-medium">
              {rutas.reduce((sum, r) => sum + r.totalAreasLimpieza, 0)}
            </span>
          </div>
          <div>
            <span className="text-gray-500">Tareas Realizadas:</span>
            <span className="ml-2 font-medium">
              {rutas.reduce((sum, r) => sum + r.totalTareasRealizadas, 0)}
            </span>
          </div>
          <div>
            <span className="text-gray-500">Tareas Pendientes:</span>
            <span className="ml-2 font-medium">
              {rutas.reduce((sum, r) => sum + r.totalTareasPendientes, 0)}
            </span>
          </div>
        </div>
        
        {rutas.length > 0 && (
          <div className="mt-4">
            <h5 className="text-sm font-medium text-gray-700 mb-2">Usuarios con Rutas:</h5>
            <div className="flex flex-wrap gap-2">
              {Array.from(new Set(rutas.map(r => r.usuarioNombre))).map(nombre => (
                <span key={nombre} className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                  {nombre}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RutasReportGenerator;
